/**
 * Probing a host's `GET /health` (`@acp/api-contract/health.js`): reachable
 * or not, how fast, and what it says about itself. Shared by `acp status` and
 * the SPA's connectivity manager, so the CLI verifies exactly what the panel
 * relies on (D29).
 *
 * Never throws: every failure is a result the caller shows — `timeout`, a
 * `network` failure (refused, DNS, CORS, TLS: a browser cannot tell them
 * apart), an `http` status that is not 200, or an `invalid` body (reachable,
 * but not an ACP health answer — a captive portal, a proxy's error page).
 *
 * No token is sent: the probe must answer before sign-in, and a probe that
 * failed on a bad token would read as "the host is down".
 */
import { anyHealthWireSchema, type AnyHealthWire, HEALTH_PATH } from '@acp/api-contract/health.js';
import type { FetchPort } from './ports.js';

export type ProbeFailure = 'timeout' | 'network' | 'http' | 'invalid';

export type HealthProbe =
  | { readonly ok: true; readonly latencyMs: number; readonly health: AnyHealthWire }
  | { readonly ok: false; readonly latencyMs: number; readonly failure: ProbeFailure; readonly message: string };

export type ProbeDeps = {
  readonly fetch: FetchPort;
  readonly nowMs: () => number;
  /** Run `fn` after `ms`; returns a cancel function (`Timers.after`). */
  readonly after: (ms: number, fn: () => void) => () => void;
};

/** How long a probe waits before calling a host unreachable: generous for a cloud round trip, short enough to notice a dead LAN node. */
export const PROBE_TIMEOUT_MS = 5_000;

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export async function probeHealth(deps: ProbeDeps, apiUrl: string, timeoutMs: number): Promise<HealthProbe> {
  const started = deps.nowMs();
  const elapsed = (): number => Math.max(0, deps.nowMs() - started);
  let cancelTimer: (() => void) | undefined;
  const timedOut = new Promise<'timeout'>((resolve) => {
    cancelTimer = deps.after(timeoutMs, () => resolve('timeout'));
  });
  const request = (async () => {
    const response = await deps.fetch(`${apiUrl}${HEALTH_PATH}`, { method: 'GET', headers: { accept: 'application/json' }, body: null, timeoutMs });
    return { status: response.status, text: await response.text() };
  })();
  let answer: { status: number; text: string } | 'timeout';
  try {
    answer = await Promise.race([request, timedOut]);
  } catch (err) {
    return { ok: false, latencyMs: elapsed(), failure: 'network', message: describe(err) };
  } finally {
    cancelTimer?.();
  }
  // A late rejection of the abandoned request must not surface as unhandled.
  request.catch(() => undefined);
  if (answer === 'timeout') return { ok: false, latencyMs: elapsed(), failure: 'timeout', message: `no answer within ${String(timeoutMs)} ms` };
  const latencyMs = elapsed();
  if (answer.status !== 200) return { ok: false, latencyMs, failure: 'http', message: `HTTP ${String(answer.status)}` };
  let json: unknown;
  try {
    json = JSON.parse(answer.text);
  } catch {
    return { ok: false, latencyMs, failure: 'invalid', message: 'the answer is not JSON' };
  }
  const parsed = anyHealthWireSchema.safeParse(json);
  if (!parsed.success) return { ok: false, latencyMs, failure: 'invalid', message: 'the answer is not an ACP health report' };
  return { ok: true, latencyMs, health: parsed.data };
}
